import { createFileRoute, Link } from "@tanstack/react-router";
import { m } from "framer-motion";
import { ArrowUpRight, HandHeart, Heart, Users } from "lucide-react";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { charities } from "@/data/charities";
import { CharitySupportForm } from "@/components/site/CharitySupportForm";


export const Route = createFileRoute("/giving/")({
  head: () => ({
    meta: [
      { title: "Causes we list | Ajility" },
      {
        name: "description",
        content:
          "Causes we list: COCOS and Coptic Orphans. Donate to them directly, and apply for pro bono operating help if you run a small charity.",
      },
      { property: "og:title", content: "Causes we list | Ajility" },
      {
        property: "og:description",
        content:
          "Causes we list: COCOS and Coptic Orphans. Donate to them directly, and apply for pro bono operating help if you run a small charity.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Causes we list | Ajility" },
      { name: "twitter:description", content: "Causes we list: COCOS and Coptic Orphans. Donate to them directly, and apply for pro bono operating help if you run a small charity.", },
      ...ogImage("giving", "Causes we list"),
      ogUrl("/giving"),
    ],
    links: canonicalLinks("/giving"),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Giving", path: "/giving" }])),
      jsonLd(webPageSchema({ image: "giving",
        path: "/giving",
        name: "Causes we list | Ajility",
        description: "Causes we list: COCOS and Coptic Orphans. Donate to them directly, and apply for pro bono operating help if you run a small charity.",
        type: "CollectionPage",
      })),
    ],
  }),
  component: GivingPage,
});

const ways = [
  {
    icon: Users,
    title: "Operating help, pro bono",
    body: "Small charities carry the same operating problems as founder-led businesses: unclear roles, no rhythm, no numbers. Where the work fits, we take on a limited number of pro bono engagements each year.",
  },
  {
    icon: HandHeart,
    title: "Give directly",
    body: "The two causes listed below take donations through their own secure channels. Use the links on this page and the money reaches them without passing through anyone else.",
  },
  {
    icon: Heart,
    title: "Tell us about a fundraiser",
    body: "If you are running a fundraiser, or your organisation needs a hand with its operations, send the details. We read every one and reply either way.",
  },
];

function GivingPage() {
  return (
    <>
      <section className="bg-midnight pb-20 pt-32 text-frost">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <m.p
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-sm font-semibold uppercase tracking-widest text-silver"
            >
              Giving back
            </m.p>
            <m.h1
              initial={{ y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl"
            >
              Causes we list.
            </m.h1>
            <m.p
              initial={{ y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 text-lg leading-relaxed text-frost/80"
            >
              Two causes we list, and pro bono operating help for small charities that have
              outgrown their systems. Where the work fits, we take on a limited number of pro
              bono engagements each year.
            </m.p>
          </div>
        </div>
      </section>

      {/* Ways we support */}
      <section className="bg-surface py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-6 md:grid-cols-3">
            {ways.map((w, i) => (
              <m.div
                key={w.title}
                initial={{ y: 20 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-3xl border border-border bg-card p-8 shadow-soft"
              >
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-midnight text-steel-on-dark">
                  <w.icon className="h-6 w-6" />
                </div>
                <h2 className="mt-6 font-serif text-2xl text-midnight">{w.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{w.body}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* Charities */}
      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-serif text-3xl text-midnight sm:text-4xl">Where to give</h2>
          <p className="mt-3 max-w-2xl text-charcoal/75">
            Donations are made on each organisation's own website. Ajility does not solicit or
            receive donations on their behalf, receives nothing from them, and is not affiliated
            with either organisation. Statements about their programs are their own.
          </p>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {charities.map((c, i) => (
              <m.article
                key={c.name}
                initial={{ y: 20 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex flex-col rounded-3xl border border-border bg-card p-8 shadow-soft"
              >
                <div className="flex h-40 w-full items-center justify-center rounded-2xl bg-surface p-6">
                  <img
                    src={c.logo}
                    alt={`${c.name} logo`}
                    loading="lazy"
                    width={480}
                    height={320}
                    className={`max-h-24 w-auto max-w-full object-contain ${
                      c.slug === "cocos"
                        ? "transition duration-300 ease-out hover:scale-105 hover:drop-shadow-[0_10px_20px_rgba(245,158,11,0.2)]"
                        : ""
                    }`}
                  />
                </div>


                <h3 className="mt-6 font-serif text-2xl text-midnight">{c.name}</h3>
                <p className="mt-1 text-sm font-semibold text-steel">{c.focus}</p>
                <p className="mt-4 text-sm leading-relaxed text-charcoal">{c.blurb}</p>

                <dl className="mt-6 space-y-3 border-t border-border pt-6 text-sm">
                  {c.details.map((d) => (
                    <div key={d.label} className="flex flex-col gap-1 sm:flex-row sm:gap-4">
                      <dt className="w-32 shrink-0 font-semibold uppercase tracking-wider text-xs text-steel">
                        {d.label}
                      </dt>
                      <dd className="text-charcoal">{d.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-auto flex flex-col gap-3 pt-8 sm:flex-row">
                  <a
                    href={c.donate}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-midnight px-7 text-sm font-semibold text-frost transition hover:bg-midnight-deep"
                  >
                    Visit their donation page
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <Link
                    to={c.path}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-border px-7 text-sm font-semibold text-midnight transition hover:border-steel hover:text-steel"
                  >
                    Read their story
                  </Link>
                </div>
              </m.article>
            ))}
          </div>
        </div>
      </section>

      {/* Open to more */}
      <section className="bg-midnight py-20 text-frost lg:py-24">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl">Need operating help for a charity?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-frost/80">
            If you run a small charity or a fundraiser and the operations have outgrown the people
            running them, send the details. Where the work fits, we take on a limited number of
            pro bono engagements each year. We read every request and reply either way.
          </p>
          <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
            <a
              href="#charity-support"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-frost px-7 text-base font-semibold text-midnight-deep transition hover:bg-white"
            >
              Apply for pro bono help
            </a>
            <a
              href="mailto:adam@ajility.com.au?subject=Fundraiser%20support"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/20 px-7 text-base font-semibold text-frost transition hover:border-steel hover:text-frost"
            >
              adam@ajility.com.au
            </a>
          </div>
        </div>
      </section>

      <CharitySupportForm />
    </>

  );
}
